import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { BottomNav } from "@/components/BottomNav";
import { authGuard } from "@/lib/authGuard";
import { useAppStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  ShieldCheck,
  ArrowLeft,
  ArrowRight,
  Lock,
} from "lucide-react";
import { getTestProjectsByCategory } from "@/features/tests/testData";
import type { TestProject } from "@/features/tests/TestCard";
import { getCompletedTests, markTestCompleted, isTestCompleted } from "@/lib/completedTests";

export const Route = createFileRoute("/submit-test")({
  head: () => ({
    meta: [
      { title: "Submit Test — PataTasks" },
      { name: "description", content: "Submit your completed app or web test." },
    ],
  }),
  beforeLoad: async () => {
    const store = await authGuard();
    if (!store.isAuthenticated) {
      throw redirect({ to: "/login" });
    }
  },
  component: SubmitTestPage,
});

type Stage = "loading" | "already_completed" | "quiz" | "success" | "analyzing";

function SubmitTestPage() {
  const [project, setProject] = useState<TestProject | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [checklistAnswers, setChecklistAnswers] = useState<Record<string, string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [started, setStarted] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [stage, setStage] = useState<Stage>("loading");
  const [step, setStep] = useState(0);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzingStep, setAnalyzingStep] = useState(0);
  const [direction, setDirection] = useState<"forward" | "backward">("forward");

  const analyzingSteps = [
    "Checking response completeness...",
    "Validating answer consistency...",
    "Running attention checks...",
    "Finalizing verification...",
  ];

  const autoAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    const pendingTestId =
      typeof window !== "undefined" ? localStorage.getItem("pending_test_id") : null;
    if (pendingTestId) {
      const allProjects = getTestProjectsByCategory();
      const found = allProjects.find((p) => p.id === pendingTestId);
      if (found) {
        setProject(found);
        isTestCompleted(found.id).then((done) => {
          if (!cancelled) {
            setStage(done ? "already_completed" : "quiz");
          }
        });
        return;
      }
    }
    if (!cancelled) {
      toast.error("No test selected");
      window.history.back();
    }
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!analyzing) return;

    const interval = setInterval(() => {
      setAnalyzingStep((s) => {
        if (s < analyzingSteps.length - 1) return s + 1;
        clearInterval(interval);
        return s;
      });
    }, 1200);

    return () => clearInterval(interval);
  }, [analyzing]);

  useEffect(() => {
    return () => {
      if (autoAdvanceRef.current) {
        clearTimeout(autoAdvanceRef.current);
      }
    };
  }, []);

  if (!project) {
    return (
      <div className="bg-background min-h-screen pb-20 md:pb-0">
        <main className="mx-auto max-w-3xl px-4 py-10">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  const totalQuestions = project.questions.length;
  const currentQuestion = project.questions[step];
  const answeredCount = project.questions.filter((q) => {
    if (q.type === "task_check") {
      return checklistAnswers[q.id]?.length === q.checklist?.length;
    }
    return answers[q.id]?.trim();
  }).length;
  const progressPercent = totalQuestions > 0 ? ((step + 1) / totalQuestions) * 100 : 0;

  const isCurrentAnswered = () => {
    if (!currentQuestion) return false;
    if (currentQuestion.type === "task_check") {
      return (
        (checklistAnswers[currentQuestion.id]?.length || 0) === currentQuestion.checklist?.length
      );
    }
    return answers[currentQuestion.id]?.trim();
  };

  const allAnswered = answeredCount === totalQuestions;

  const handleStartTest = () => {
    if (!agreed) {
      toast.error("Please confirm you meet the requirements");
      return;
    }
    setStarted(true);
    setStartTime(Date.now());
    setStep(0);
  };

  const handleNext = () => {
    if (!isCurrentAnswered()) {
      toast.error("Please answer this question before continuing");
      return;
    }
    setDirection("forward");
    if (step < totalQuestions - 1) {
      setStep((s) => s + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setDirection("backward");
      setStep((s) => s - 1);
    }
  };

  const handleSingleSelect = (value: string) => {
    if (!currentQuestion) return;
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: value }));

    if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current);
    autoAdvanceRef.current = setTimeout(() => {
      setDirection("forward");
      if (step < totalQuestions - 1) {
        setStep((s) => s + 1);
      } else {
        handleSubmit();
      }
    }, 300);
  };

  const handleSubmit = async () => {
    if (!allAnswered) {
      toast.error("Please answer all questions");
      return;
    }

    const elapsedSec = startTime ? Math.floor((Date.now() - startTime) / 1000) : 0;

    if (elapsedSec < 30) {
      toast.info("Please take your time to answer thoughtfully.");
    }

    setAnalyzing(true);
    await new Promise((r) => setTimeout(r, 5000));

    setIsSubmitting(true);
    try {
      const res = await fetch("/api/submissions", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          testId: project.id,
          testTitle: project.title,
          answers,
          reward: project.reward,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to submit test");
      }

      await markTestCompleted(project.id);
      setSubmitted(true);
      toast.success(
        "Test completed successfully! Your reward has been credited to your wallet instantly.",
      );
      await useAppStore.getState().refreshAll();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Submission failed");
    } finally {
      setIsSubmitting(false);
      setAnalyzing(false);
    }
  };

  if (stage === "already_completed") {
    return (
      <div className="bg-background min-h-screen pb-20 md:pb-0">
        <main className="mx-auto max-w-3xl px-4 py-10">
          <div className="flex flex-col items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 p-8 shadow-sm text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-200">
              <Lock className="h-6 w-6 text-slate-500" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">Test Locked</h1>
            <p className="mt-2 text-sm text-slate-600">
              You've already completed this test. It cannot be retaken.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span>Completed</span>
            </div>
            <Button className="mt-6" onClick={() => window.history.back()}>
              Browse Available Tests
            </Button>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="bg-background min-h-screen pb-20 md:pb-0">
        <main className="mx-auto max-w-3xl px-4 py-10">
          <div className="flex flex-col items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 p-8 shadow-sm text-center">
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-emerald-600" />
            <h1 className="text-2xl font-bold text-foreground">Test Completed</h1>
            <p className="mt-2 text-sm text-slate-700">
              Your feedback was submitted successfully! Check Your Wallet For Reward.
            </p>
            <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-3 text-xs text-blue-700">
              Verified tests contribute to your eligibility for higher-paying tasks.
            </div>
            <Button className="mt-6" onClick={() => window.history.back()}>
              Back to Tests
            </Button>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  if (analyzing) {
    return (
      <div className="bg-background min-h-screen pb-20 md:pb-0">
        <main className="mx-auto max-w-3xl px-4 py-10">
          <div className="flex flex-col items-center justify-center rounded-3xl border border-blue-200 bg-blue-50 p-8 shadow-sm text-center">
            <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-blue-600" />
            <h1 className="text-2xl font-bold text-foreground">Analyzing Responses</h1>
            <div className="mt-4 space-y-2">
              {analyzingSteps.map((stepText, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  {idx < analyzingStep ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : idx === analyzingStep ? (
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  ) : (
                    <div className="h-4 w-4 rounded-full border-2 border-slate-300" />
                  )}
                  <span className={idx <= analyzingStep ? "text-slate-700" : "text-slate-400"}>
                    {stepText}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  if (!started) {
    return (
      <div className="bg-background min-h-screen pb-20 md:pb-0">
        <main className="mx-auto max-w-3xl px-4 py-10">
          <div className="rounded-3xl border border-blue-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="h-5 w-5 text-blue-600" />
              <h1 className="text-2xl font-bold tracking-tight text-foreground">{project.title}</h1>
            </div>

            <div className="mb-6 rounded-xl border-2 border-blue-200 bg-blue-50/40 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Test Requirements</h3>
              <div className="flex flex-wrap gap-2">
                {(project.requirements || []).map((req, idx) => (
                  <span
                    key={idx}
                    className="flex items-center gap-1 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-slate-700 border border-blue-100"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 text-blue-600" />
                    {req}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-6 rounded-xl border-2 border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Before you start</p>
                  <ul className="mt-2 list-disc list-inside text-xs text-amber-700 space-y-1">
                    <li>Ensure you meet all requirements listed above</li>
                    <li>Read all instructions carefully before proceeding</li>
                    <li>Answer all questions honestly and thoroughly</li>
                    <li>This test requires approximately {project.estimatedTime || "3 mins"}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <Checkbox
                id="agree-requirements"
                checked={agreed}
                onCheckedChange={(checked) => setAgreed(checked === true)}
              />
              <Label
                htmlFor="agree-requirements"
                className="flex-1 cursor-pointer text-sm text-slate-700"
              >
                I confirm that I meet all the requirements listed above and will complete this test
                honestly.
              </Label>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={handleStartTest}
                disabled={!agreed}
                className="bg-blue-600 hover:bg-green-600 text-white shadow-md hover:shadow-lg transition-all duration-200"
              >
                Start Test
              </Button>
            </div>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes slideInLeft {
          from { transform: translateX(-30px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
      <div className="bg-background min-h-screen pb-20 md:pb-0">
        <main className="mx-auto max-w-3xl px-4 py-10">
          <div className="mb-6">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">{project.title}</h1>
            <p className="mt-1 text-xs text-slate-500">
              This test verifies real user interaction with apps. Your responses are checked for
              consistency and attention to ensure high-quality feedback.
            </p>
          </div>

          <div className="mb-6 rounded-xl border border-blue-200 bg-white p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-slate-600">
                Question {step + 1} of {totalQuestions}
              </span>
              <span className="flex items-center gap-1 text-xs text-slate-500">
                <Clock className="h-3 w-3" />
                {started ? `${Math.floor((Date.now() - startTime!) / 60000)} min` : "0 min"}
              </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100">
              <div
                className="h-2 rounded-full bg-blue-600 transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <div className="rounded-3xl border border-blue-200 bg-white p-6 shadow-sm">
            <div
              key={currentQuestion.id}
              style={{
                animation:
                  direction === "forward" ? "slideInRight 0.3s ease" : "slideInLeft 0.3s ease",
              }}
            >
              <div className="space-y-3">
                <p className="text-sm font-semibold text-foreground">
                  {step + 1}. {currentQuestion.text}
                </p>
                {currentQuestion.type === "multiple_choice" && currentQuestion.options ? (
                  <RadioGroup
                    value={answers[currentQuestion.id] || ""}
                    onValueChange={handleSingleSelect}
                  >
                    {currentQuestion.options.map((option) => (
                      <Label
                        key={option}
                        htmlFor={`${currentQuestion.id}-${option}`}
                        className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50/40 px-4 py-3 transition-all duration-200 hover:border-blue-300 hover:shadow-sm cursor-pointer"
                      >
                        <RadioGroupItem value={option} id={`${currentQuestion.id}-${option}`} />
                        <span className="flex-1 text-sm font-medium text-slate-700">{option}</span>
                      </Label>
                    ))}
                  </RadioGroup>
                ) : currentQuestion.type === "task_check" && currentQuestion.checklist ? (
                  <div className="space-y-3">
                    {currentQuestion.checklist.map((item) => {
                      const isChecked = checklistAnswers[currentQuestion.id]?.includes(item);
                      return (
                        <div
                          key={item}
                          className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50/40 px-4 py-3"
                        >
                          <Checkbox
                            id={`${currentQuestion.id}-${item}`}
                            checked={isChecked}
                            onCheckedChange={(checked) => {
                              setChecklistAnswers((prev) => {
                                const current = prev[currentQuestion.id] || [];
                                const next = checked
                                  ? [...current, item]
                                  : current.filter((i) => i !== item);
                                return { ...prev, [currentQuestion.id]: next };
                              });
                              setAnswers((prev) => ({
                                ...prev,
                                [currentQuestion.id]: JSON.stringify(
                                  (checked
                                    ? [...(checklistAnswers[currentQuestion.id] || []), item]
                                    : (checklistAnswers[currentQuestion.id] || []).filter(
                                        (i) => i !== item,
                                      )
                                  ).sort(),
                                ),
                              }));
                            }}
                          />
                          <Label
                            htmlFor={`${currentQuestion.id}-${item}`}
                            className="flex-1 cursor-pointer text-sm font-medium text-slate-700"
                          >
                            {item}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                ) : currentQuestion.type === "rating" ? (
                  <RadioGroup
                    value={answers[currentQuestion.id] || ""}
                    onValueChange={handleSingleSelect}
                  >
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Label
                        key={star}
                        htmlFor={`${currentQuestion.id}-${star}`}
                        className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50/40 px-4 py-3 transition-all duration-200 hover:border-blue-300 hover:shadow-sm cursor-pointer"
                      >
                        <RadioGroupItem value={String(star)} id={`${currentQuestion.id}-${star}`} />
                        <span className="flex-1 text-sm font-medium text-slate-700">{star}</span>
                      </Label>
                    ))}
                  </RadioGroup>
                ) : (
                  <Textarea
                    value={answers[currentQuestion.id] || ""}
                    onChange={(e) =>
                      setAnswers((prev) => ({ ...prev, [currentQuestion.id]: e.target.value }))
                    }
                    placeholder="Your answer..."
                    className="min-h-[100px]"
                  />
                )}
              </div>
            </div>

            <div className="mt-8 flex justify-between">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={step === 0}
                className="gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={!isCurrentAnswered() || isSubmitting}
                className="bg-blue-600 hover:bg-green-600 text-white gap-2 shadow-md hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-md"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : step === totalQuestions - 1 ? (
                  "Submit Test"
                ) : (
                  <>
                    Next
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>

            <div className="mt-6 rounded-xl border border-blue-100 bg-blue-50 p-3 text-xs text-blue-700 space-y-1">
              <p className="font-medium">What happens after?</p>
              <p>Verified tests contribute to your eligibility for higher-paying tasks.</p>
              <p className="mt-2 font-medium">Privacy reassurance</p>
              <p>We do not collect personal data — only your interaction feedback.</p>
            </div>
          </div>
        </main>
        <BottomNav />
      </div>
    </>
  );
}
